import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { FC, useState } from 'react';
import { Navigate, useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { AmountInput, Button, Currencies, Icon, Input, Modal, ModalSize, formatNumber } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { BankAccountCard, RenderFields, RestrictedBanner } from '~/components';
import { loanQueryKey } from '~/lib/api';
import { LoanRepository } from '~/repositories/LoanRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FormLabel, Inner, Text } from '~/styles';

import { LoanProps } from './Loan';

interface Params {
  id: string;
}

interface LoanForm {
  description: string;
  isRevolving: boolean;
  maturityDate?: string;
  maxPrincipalBalance?: number;
  currency?: string;
  status?: string;
  primarySignerEntityId?: string;
  entityName?: string;
  createdAt?: string;
  chargedOffAt?: string;
  paidOffAt?: string;
  delinquentAt?: string;
  disputedAt?: string;
}

const FormElementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const AmountHint = styled(Text)`
  display: block;
  padding: 2px 1px 0;
`;

interface ApplyCollateralModalProps {
  lockedAmount: number;
  currencyCode: string;
  isSubmitting: boolean;
  onClose: () => void;
  onApply: (request: { amount: number; description?: string }) => void;
}

const ApplyCollateralModal: FC<ApplyCollateralModalProps> = ({
  lockedAmount,
  currencyCode,
  isSubmitting,
  onClose,
  onApply,
}) => {
  const [amount, setAmount] = useState<number | undefined>();
  const [description, setDescription] = useState('');
  const exceedsLockedAmount = amount !== undefined && amount > lockedAmount;
  const canApply = amount !== undefined && amount > 0 && !exceedsLockedAmount && !isSubmitting;

  const handleApply = () => {
    if (!canApply) return;
    onApply({ amount, description: description || undefined });
  };

  return (
    <Modal size={ModalSize.Small} isOpen onClose={onClose}>
      <Modal.IconCircle type="primary" icon="HandCoins" />

      <Modal.Content>
        <Modal.Headline fullWidth>Apply Collateral</Modal.Headline>
        <Modal.Paragraph fullWidth>Apply locked collateral to this loan's principal.</Modal.Paragraph>

        <FormElementWrapper>
          <FormLabel>Amount</FormLabel>
          <AmountInput
            value={amount}
            onChange={setAmount}
            currencyCode={currencyCode}
            fixedCurrencyCode={currencyCode}
            currencyList={Currencies}
            showCurrencyCode
            hasError={exceedsLockedAmount && 'Amount exceeds locked collateral'}
            size="small"
          />
          <AmountHint size={12} color="light">
            Available locked collateral: {formatNumber(lockedAmount, currencyCode)}
          </AmountHint>
        </FormElementWrapper>
        <FormElementWrapper>
          <FormLabel>
            Description <small>(optional)</small>
          </FormLabel>
          <Input
            value={description}
            onChange={setDescription}
            maxLength={127}
            size="small"
            placeholder="Description of the collateral application"
          />
        </FormElementWrapper>
      </Modal.Content>

      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleApply}
          size="small"
          isDisabled={!canApply}
          isLoading={isSubmitting}
        >
          Apply Collateral
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const PageLoanCollateral: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;
  const { loan } = useOutletContext<LoanProps>();
  const canWrite = useSessionStore((state) => state.isAuthorized({ permission: 'loans', permissionLevel: 'write' }));
  const [isApplyOpen, setIsApplyOpen] = useState(false);

  const currencyCode = loan?.currency ?? 'USD';
  const lockedAmount = loan?.collateralLockedAmount ?? 0;
  const collateralBankAccountId = loan?.collateralBankAccountId;

  const applyCollateralMutation = useMutation({
    mutationFn: (request: { amount: number; description?: string }) => {
      if (!id) throw new Error('Loan ID is required');
      return LoanRepository.applyCollateral(id, {
        amount: request.amount,
        currencyCode,
        description: request.description,
      });
    },
    onSuccess: () => {
      setIsApplyOpen(false);
      queryClient.invalidateQueries({ queryKey: loanQueryKey(id!) });
      addSuccessNotification({ content: 'Collateral applied to loan' });
    },
    onError: (error: any) => {
      addDangerNotification({ content: error.message });
    },
  });

  if (!loan) {
    return null;
  }

  if (loan.type !== 'secured') {
    return <Navigate to={`${ROUTE.LOANS}/edit/${id}`} replace />;
  }

  return (
    <>
      {!canWrite && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}

      {RenderFields<LoanForm>({
        sections: [
          {
            headline: 'Collateral',
            fields: [
              {
                id: 'currency',
                label: 'Collateral locked',
                children: () => (
                  <Text size={14} weight="medium" color="foreground">
                    {formatNumber(lockedAmount, currencyCode)}
                  </Text>
                ),
              },
            ],
          },
          {
            headline: 'Collateral Account',
            hide: !collateralBankAccountId,
            fields: [
              {
                id: 'description',
                label: 'Account',
                children: () => <BankAccountCard bankAccountId={collateralBankAccountId!} />,
              },
            ],
          },
        ],
      })}

      {canWrite && (
        <Inner pt={0} px="var(--page-margin)">
          <Button
            variant="primary"
            size="small"
            onClick={() => setIsApplyOpen(true)}
            isDisabled={lockedAmount <= 0}
            icon={<Icon.HandCoins />}
          >
            Apply Collateral
          </Button>
        </Inner>
      )}

      {isApplyOpen && loan && (
        <ApplyCollateralModal
          lockedAmount={lockedAmount}
          currencyCode={currencyCode}
          isSubmitting={applyCollateralMutation.isPending}
          onClose={() => setIsApplyOpen(false)}
          onApply={(request) => applyCollateralMutation.mutate(request)}
        />
      )}
    </>
  );
};
